import {useSequenceValidation} from '@/modules/patients-therapies/hooks'
import {getParameterDefaultValuesNew} from '@/modules/patients/functions'
import {type ParameterSchemaNew} from '@/modules/patients/schemas'
import {Box, Button, ButtonGroup, Text} from '@chakra-ui/react'
import {PatientTherapySequence} from '@cypro/engine/schema'
import {Tooltip} from '@cypro/ui/components'
import {withForm} from '@cypro/ui/integrations/form'
import {useStore} from '@tanstack/react-form'
import {useTranslate} from '@tolgee/react'

// Panel for prescriptions awaiting the doctor's validation. Shows a distinct copy
// after the doctor changed an already validated prescription (engine reverts the
// validation on edit).
export const DoctorValidation = withForm({
  defaultValues: getParameterDefaultValuesNew() as ParameterSchemaNew,
  props: {} as {
    sequence: PatientTherapySequence
    changed?: boolean
    isSaveDisabled?: boolean
    saveDisabledReason?: string
    onSaveAndValidate?: (validate: () => Promise<void>) => void
  },
  render: function Render({
    form,
    sequence,
    changed = false,
    isSaveDisabled = false,
    saveDisabledReason,
    onSaveAndValidate,
  }) {
    const {t} = useTranslate('common')
    const {t: tActivity} = useTranslate('activity')
    const {canDoctorValidate, onDoctorValidate} = useSequenceValidation(sequence)
    const isDirty = useStore(form.store, state => !state.isDefaultValue)

    const title = changed ? t('prescription_changed') : t('ready_for_doctor_validation')
    const description = changed ? t('previous_validations_removed') : t('medication_was_prescribed')

    const noPermission = !canDoctorValidate
    const heading = noPermission ? t('validation_not_available') : title
    const subline = noPermission ? t('validation_no_role') : description

    function onClickValidate() {
      // always run the normal save pipeline first: a pristine form can still fail the
      // required-field or overdose checks that validation must not bypass
      if (onSaveAndValidate) {
        onSaveAndValidate(async () => {
          await onDoctorValidate()
        })
        return
      }

      void onDoctorValidate()
    }

    return (
      <Box width="100%">
        <Box spaceY="2" width="100%">
          <Box
            display="flex"
            flexDirection="column"
            width={'100%'}
            alignItems={{base: 'center', md: 'flex-start'}}
            justifyContent={{base: 'center', md: 'flex-start'}}
          >
            <Text fontSize="sm" fontWeight="medium" color={noPermission ? 'fg.muted' : undefined}>
              {heading}
            </Text>
            <Text display={{base: 'none', md: 'inline-flex'}} color="fg.muted">
              {subline}
            </Text>
          </Box>

          <ButtonGroup display="flex" gap="2" size="md" colorPalette="cyan" width="100%">
            <Tooltip content={isSaveDisabled && saveDisabledReason ? t(saveDisabledReason) : undefined}>
              <Button
                variant="subtle"
                colorPalette="cyan"
                disabled={!canDoctorValidate || isSaveDisabled}
                width="100%"
                onClick={onClickValidate}
              >
                {isDirty ? t('save_and_validate') : tActivity('actions.DOCTOR_VALIDATE')}
              </Button>
            </Tooltip>
          </ButtonGroup>
        </Box>
      </Box>
    )
  },
})
